import React, { useState, useEffect } from 'react';
import { Col, Dropdown, DropdownMenu, DropdownToggle, Nav, NavItem, NavLink, Row, TabContent, TabPane } from 'reactstrap';
import { Link, useNavigate } from 'react-router-dom';
import classnames from 'classnames';

//import images
import bell from "../../assets/images/svg/bell.svg";

//SimpleBar
import SimpleBar from "simplebar-react";
import { getAdminNotifications, markAsRead, IAdminNotification, AdminNotificationsResponse } from "../../pages/Notifications/_requests";
import moment from "moment";
import { toast, ToastContainer } from "react-toastify";

const NotificationDropdown = () => {
    const navigate = useNavigate();
    //Dropdown Toggle
    const [isNotificationDropdown, setIsNotificationDropdown] = useState<boolean>(false);
    const [notifications, setNotifications] = useState<IAdminNotification[]>([]);
    const [unreadCount, setUnreadCount] = useState<number>(0);
    const [prevCount, setPrevCount] = useState<number>(0);

    const toggleNotificationDropdown = () => {
        setIsNotificationDropdown(!isNotificationDropdown);
    };

    const requestPermission = () => {
        if ("Notification" in window) {
            Notification.requestPermission();
        }
    };

    const fetchNotifications = async () => {
        try {
            const response = await getAdminNotifications();
            if (response.status) {
                setNotifications(response.data);
                setUnreadCount(response.unreadCount);
            }
        } catch (error) {
            console.error("Error fetching notifications", error);
        }
    };

    useEffect(() => {
        requestPermission();
        fetchNotifications();

        // Listen for socket events to refresh list in real-time
        const handleNewNotification = () => {
            fetchNotifications();
        };

        window.addEventListener("new-socket-notification", handleNewNotification);
        
        // Polling as fallback (keep it but maybe slower or just for safety)
        const interval = setInterval(fetchNotifications, 30000); 

        return () => {
            clearInterval(interval);
            window.removeEventListener("new-socket-notification", handleNewNotification);
        };
    }, []); 

    const handleNotificationClick = async (notif: any) => {
        if (!notif.is_read) {
            try {
                await markAsRead(notif.id);
                fetchNotifications();
            } catch (error) {
                console.error("Error marking as read", error);
            }
        }
        
        // Redirect logic
        if (notif.category === "ORDER") {
            navigate("/orders");
        } else if (notif.category === "SUBSCRIPTION") {
            navigate("/user-subscription");
        } else {
            navigate("/notifications");
        }
        setIsNotificationDropdown(false);
    };

    //Tab 
    const [activeTab, setActiveTab] = useState('1');
    const toggleTab = (tab: any) => {
        if (activeTab !== tab) {
            setActiveTab(tab);
        }
    };
    return (
        <React.Fragment>
            <Dropdown isOpen={isNotificationDropdown} toggle={toggleNotificationDropdown} className="topbar-head-dropdown ms-1 header-item">
                <DropdownToggle type="button" tag="button" className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle shadow-none">
                    <i className='bx bx-bell fs-22'></i>
                    {unreadCount > 0 && (
                        <span className="position-absolute topbar-badge fs-10 translate-middle badge rounded-pill bg-danger">
                            {unreadCount}
                            <span className="visually-hidden">unread messages</span>
                        </span>
                    )}
                </DropdownToggle>
                <DropdownMenu className="dropdown-menu-lg dropdown-menu-end p-0">
                    <div className="dropdown-head bg-primary bg-pattern rounded-top">
                        <div className="p-3">
                            <Row className="align-items-center">
                                <Col>
                                    <h6 className="m-0 fs-16 fw-semibold text-white"> Notifications </h6>
                                </Col>
                                <div className="col-auto dropdown-tabs">
                                    <span className="badge bg-light-subtle text-body fs-13"> {unreadCount} New</span>
                                    <Link to="#" className="text-white-50 ms-2" onClick={async () => {
                                        // Simple logic: mark visible ones as read
                                        for (const n of notifications) {
                                            if (!n.is_read) await markAsRead(n.id);
                                        }
                                        fetchNotifications();
                                    }}>Mark all as read</Link>
                                </div>
                            </Row>
                        </div>

                        <div className="px-2 pt-2">
                            <Nav className="nav-tabs dropdown-tabs nav-tabs-custom">
                                <NavItem>
                                    <NavLink
                                        href="#"
                                        className={classnames({ active: activeTab === '1' })}
                                        onClick={() => { toggleTab('1'); }}
                                    >
                                        All ({notifications.length})
                                    </NavLink>
                                </NavItem>
                            </Nav>
                        </div>

                    </div>

                    <TabContent activeTab={activeTab}>
                        <TabPane tabId="1" className="py-2 ps-2">
                            <SimpleBar style={{ maxHeight: "300px" }} className="pe-2">
                                {notifications.length > 0 ? (
                                    notifications.map((notif, index) => (
                                        <div 
                                            key={index} 
                                            className={classnames("text-reset notification-item d-block dropdown-item position-relative", { "active": !notif.is_read })}
                                            onClick={() => handleNotificationClick(notif)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <div className="d-flex">
                                                <div className="avatar-xs me-3">
                                                    <span className={classnames("avatar-title rounded-circle fs-16", 
                                                        notif.category === 'ORDER' ? "bg-info-subtle text-info" : "bg-success-subtle text-success")}>
                                                        <i className={notif.category === 'ORDER' ? "bx bx-shopping-bag" : "bx bx-calendar-check"}></i>
                                                    </span>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <h6 className="mt-0 mb-1 fs-13 fw-semibold">{notif.title}</h6>
                                                    <div className="fs-13 text-muted">
                                                        <p className="mb-1">{notif.description}</p>
                                                    </div>
                                                    <p className="mb-0 fs-11 fw-medium text-uppercase text-muted">
                                                        <span><i className="mdi mdi-clock-outline"></i> {moment(notif.created_at).fromNow()}</span>
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center pb-5 mt-2">
                                        <div className="avatar-lg mx-auto mt-2">
                                            <div className="avatar-title bg-light text-primary rounded-circle display-4">
                                                <i className="ri-notification-off-line"></i>
                                            </div>
                                        </div>
                                        <h6 className="fs-16 fw-semibold lh-base mt-2">No notifications yet </h6>
                                    </div>
                                )}

                                <div className="my-3 text-center">
                                    <button 
                                        type="button" 
                                        className="btn btn-soft-success waves-effect waves-light"
                                        onClick={() => { navigate("/notifications"); setIsNotificationDropdown(false); }}
                                    >
                                        View All Notifications <i className="ri-arrow-right-line align-middle"></i>
                                    </button>
                                </div>
                            </SimpleBar>
                        </TabPane>
                    </TabContent>
                </DropdownMenu>
            </Dropdown>
        </React.Fragment>
    );
};

export default NotificationDropdown;